/**
 * Marketing › Advertising's three sources (wave 16, cards 3d9c00e6…36ec and …60a6):
 *
 * - `GET /api/v1/analytics/advertising/overview` — the KPI row, Meta's own figures
 *   beside TmT's, every campaign with spend, the top campaigns by ROAS and how the
 *   campaign rows add up to the KPI (`modules/advertising_overview/README.md`);
 * - `GET …/overview/trend` — ad spend and attributed revenue by day or week, beside
 *   every completed order and its gross (`all_sales`);
 * - `GET …/reconciliation` (+ `/export`) — Meta-reported against TmT-attributed
 *   purchases and revenue per campaign, ad set or ad, with the gap and why the two
 *   differ (`modules/ad_attribution_reconciliation/README.md`).
 *
 * `analytics.read` to read, `exports.create` for the CSV; all behind the
 * `advertising_meta` flag, off answering `404`. Every figure says whose it is —
 * `tmt` (the Suite's own attribution under `model`) or `meta` (reported by Meta) —
 * and the two are never added together. `all_sales` is neither: every completed order
 * over Meta's spend, so it includes sales the ads did not drive. Money is integer minor units of the
 * response's currency; `delta_pct` and the reconciliation's `*_pct` are percents.
 */

import type { Environment } from '@/lib/api'
import type { GlobalFilters } from '@/lib/filters'

import { freshnessFromWire, type AdvertisingFreshnessOut } from './advertising'
import { dataQualityFromWire, type DataQuality, type DataQualityOut } from './dataQuality'
import { overviewSearch } from './overview'
import type { AdvertisingFreshness, AdvertisingLevel, AttributionModel } from './types'

// --- vocabulary -------------------------------------------------------------------

/** The ad platforms the page reads. Meta is the only one the Suite imports today. */
export const AD_PROVIDERS = ['meta'] as const
export type AdProvider = (typeof AD_PROVIDERS)[number]

export const AD_PROVIDER_LABELS: Record<AdProvider, string> = { meta: 'Meta Ads' }

/** The KPI row's keys, in the API's display order (`definitions.KPIS`). */
export const AD_KPI_KEYS = [
  'ad_spend',
  'attributed_revenue',
  'roas',
  'revenue_per_ad_spend',
  'purchases',
  'cpa',
  'cost_per_new_customer',
  'ctr',
  'cpc',
  'cpm',
] as const
export type AdKpiKey = (typeof AD_KPI_KEYS)[number]

export type AdKpiUnit = 'currency_minor' | 'count' | 'ratio'

/**
 * Whose number a figure is: TmT's own first-party attribution, reported by Meta, or
 * `all_sales` — every completed order over Meta's spend, no attribution at all.
 */
export type FigureSource = 'tmt' | 'meta' | 'all_sales'

/** Why a reported KPI's `current` is null: its denominator is zero, or its sales have no currency rate. */
export type AdKpiNullReason =
  | 'no_spend'
  | 'sales_currency_rate_unavailable'
  | 'no_attributed_purchases'
  | 'no_attributed_new_customers'
  | 'no_impressions'
  | 'no_clicks'

/** Why a KPI, or the whole trend, has no value at all. */
export type AdNotAvailable = 'needs_ad_platform'

export const TREND_GRANULARITIES = ['day', 'week'] as const
export type TrendGranularity = (typeof TREND_GRANULARITIES)[number]

/** Why Meta's side of a reconciliation figure is null. */
export type MetaNotAvailable = 'attribution_window_unknown' | 'not_a_meta_object' | 'not_imported'

/** Why a reconciliation gap (or its percentage) is null. */
export type DiffNullReason = 'meta_not_available' | 'meta_zero'

/** Why a `total.meta_only` figure is null: Meta's is unknown, or TmT traced as many or more. */
export type MetaOnlyNullReason = 'meta_not_available' | 'tmt_not_below_meta'

/** The TmT-only lines, always both, in this order. */
export const RECONCILIATION_LINE_KINDS = ['tmt_not_at_level', 'tmt_unmatched_paid'] as const
export type ReconciliationLineKind = (typeof RECONCILIATION_LINE_KINDS)[number]

// --- queries ----------------------------------------------------------------------

/** The overview's parameters: the global filters' range, website and environments, plus the page's own. */
export interface AdOverviewQuery {
  filters: GlobalFilters
  provider: AdProvider
  model: AttributionModel
  /** How many `topCampaigns`, 1–20. */
  limit: number
}

export interface AdOverviewTrendQuery {
  filters: GlobalFilters
  provider: AdProvider
  model: AttributionModel
  granularity: TrendGranularity
}

/** The reconciliation is tenant-wide and production only: it takes no website or environment. */
export interface AdReconciliationQuery {
  from: string
  to: string
  level: AdvertisingLevel
  model: AttributionModel
}

// --- domain shapes ----------------------------------------------------------------

export interface AdKpi {
  key: AdKpiKey
  unit: AdKpiUnit
  source: FigureSource
  current: number | null
  previous: number | null
  /** `(current − previous) ÷ |previous| × 100`, two places: a **percent**, not a fraction. */
  deltaPct: number | null
  nullReason: AdKpiNullReason | null
}

/** Meta's own purchases, revenue, ROAS and CPA, under its attribution windows. Never TmT's. */
export interface ProviderFigures {
  source: 'meta'
  purchases: number
  revenueMinor: number
  roas: number | null
  cpaMinor: number | null
}

/** TmT's own first-party attribution under the overview's model. Never Meta's. */
export interface TmtFigures {
  source: 'tmt'
  purchases: number
  revenueMinor: number
  newCustomers: number
  cpaMinor: number | null
  roas: number | null
  costPerNewCustomerMinor: number | null
}

export interface AdCampaignRow {
  id: string
  /** Null when the campaign is no longer in the imported hierarchy (its spend still counts). */
  name: string | null
  status: string | null
  effectiveStatus: string | null
  spendMinor: number
  impressions: number
  clicks: number
  ctr: number | null
  cpcMinor: number | null
  tmt: TmtFigures
  providerReported: ProviderFigures
}

export interface ReconciliationPart {
  purchases: number
  revenueMinor: number
}

/** The KPI row against the campaign rows: `attributed = campaigns + unmatchedPaid + adCampaignsWithoutSpend − campaignCreditOffSpendRows`. */
export interface OverviewReconciliation {
  attributedRevenueMinor: number
  purchases: number
  campaigns: ReconciliationPart
  unmatchedPaid: ReconciliationPart
  adCampaignsWithoutSpend: ReconciliationPart
  campaignCreditOffSpendRows: ReconciliationPart
}

export interface DayRange {
  from: string
  to: string
}

export interface AdOverviewMeta {
  provider: AdProvider
  currency: string
  timezone: string
  range: DayRange
  previousRange: DayRange
  /** `currency_rate_unavailable` when every `previous` is null for want of a rate. */
  previousUnavailable: 'currency_rate_unavailable' | null
  environments: Environment[]
  nonProduction: boolean
  websiteId: string | null
  model: AttributionModel
  lookbackDays: number
  /** Meta's windows behind `providerReported`, e.g. `7d_click,1d_view`; empty when none is recorded. */
  attributionWindows: string[]
  asOf: AdvertisingFreshness
}

export interface AdvertisingOverview {
  provider: AdProvider
  /** Every KPI the API sent, in `AD_KPI_KEYS` order. */
  kpis: AdKpi[]
  notAvailable: Partial<Record<AdKpiKey, AdNotAvailable>>
  providerReported: { current: ProviderFigures | null; previous: ProviderFigures | null }
  /** Every campaign with spend in the range, most spend first. */
  campaigns: AdCampaignRow[]
  /** The best campaigns by TmT ROAS (no ROAS last), cut to `limit`. */
  topCampaigns: AdCampaignRow[]
  limit: number
  /** Null when the KPIs are not available. */
  reconciliation: OverviewReconciliation | null
  /**
   * The tracking baseline and coverage for the TmT figures (`data.data_quality`): coverage
   * under `meta.model` for the range and scope, the comparison is `meta.previousRange`.
   * Null from an API older than the coverage card.
   */
  dataQuality: DataQuality | null
  meta: AdOverviewMeta
}

export interface AdTrendPoint {
  /** The bucket's first and last tenant day. */
  date: string
  endDate: string
  spendMinor: number
  attributedRevenueMinor: number
  purchases: number
  /** Every completed order in scope and their gross (`all_sales`, the Overview's); null when `salesUnavailable`. */
  orders: number | null
  revenueGrossMinor: number | null
}

export interface AdTrendTotals {
  spendMinor: number
  attributedRevenueMinor: number
  purchases: number
  orders: number | null
  revenueGrossMinor: number | null
}

export interface AdvertisingOverviewTrend {
  provider: AdProvider
  granularity: TrendGranularity
  points: AdTrendPoint[]
  totals: AdTrendTotals | null
  notAvailable: AdNotAvailable | null
  /** An order in the range has no currency rate: every `orders` / `revenueGrossMinor` is null. */
  salesUnavailable: 'currency_rate_unavailable' | null
  meta: {
    currency: string
    timezone: string
    range: DayRange
    model: AttributionModel
    lookbackDays: number
  }
}

export interface MetaReported {
  providerAttributed: true
  purchases: number | null
  revenueMinor: number | null
  notAvailable: MetaNotAvailable | null
}

export interface TmtAttributed {
  tmtAttributed: true
  model: AttributionModel
  purchases: number
  revenueMinor: number
}

/** TmT − Meta, signed (negative: Meta reported more), and that as a percent of Meta's figure. */
export interface ReconciliationDiff {
  purchases: number | null
  revenueMinor: number | null
  purchasesPct: number | null
  revenuePct: number | null
  purchasesNullReason: DiffNullReason | null
  revenueNullReason: DiffNullReason | null
  purchasesPctNullReason: DiffNullReason | null
  revenuePctNullReason: DiffNullReason | null
}

export interface ReconciliationFigures {
  meta: MetaReported
  tmt: TmtAttributed
  diff: ReconciliationDiff
}

/** Meta counts we can't check: Meta − TmT when Meta reported more. Still Meta's claim, never TmT's. */
export interface ReconciliationMetaOnly {
  providerAttributed: true
  purchases: number | null
  revenueMinor: number | null
  purchasesNullReason: MetaOnlyNullReason | null
  revenueNullReason: MetaOnlyNullReason | null
}

/** `total`: both sides and the gap, plus what Meta counts beyond TmT (totals only). */
export interface ReconciliationTotal extends ReconciliationFigures {
  metaOnly: ReconciliationMetaOnly
}

export interface ReconciliationObjectRow extends ReconciliationFigures {
  id: string
  name: string | null
  /** Meta-reported spend: context only, never part of the gap. */
  spendMinor: number
  deliveryInRange: boolean
}

export interface ReconciliationLine extends ReconciliationFigures {
  kind: ReconciliationLineKind
}

export interface ReconciliationCause {
  code: string
  text: string
}

export interface AdAttributionReconciliation {
  start: string
  end: string
  level: AdvertisingLevel
  currency: string
  model: AttributionModel
  attributionWindows: string[]
  tmtLookbackDays: number
  rows: ReconciliationObjectRow[]
  lines: ReconciliationLine[]
  /** Σ rows, both sides. */
  objectsTotal: ReconciliationFigures
  /** Σ rows + Σ lines. */
  total: ReconciliationTotal
  causes: ReconciliationCause[]
  asOf: AdvertisingFreshness
}

export interface AdvertisingOverviewApi {
  advertisingOverview(query: AdOverviewQuery): Promise<AdvertisingOverview>
  advertisingOverviewTrend(query: AdOverviewTrendQuery): Promise<AdvertisingOverviewTrend>
  adAttributionReconciliation(query: AdReconciliationQuery): Promise<AdAttributionReconciliation>
  /** The same answer as a CSV file (`exports.create`); a refusal rejects with the API's error. */
  adAttributionReconciliationCsv(query: AdReconciliationQuery): Promise<Blob>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------------

export interface AdKpiOut {
  unit: AdKpiUnit
  source: FigureSource
  current: number | null
  previous: number | null
  delta_pct: number | null
  null_reason?: AdKpiNullReason | null
}

export interface ProviderFiguresOut {
  source: 'meta'
  purchases: number
  revenue_minor: number
  roas: number | null
  cpa_minor: number | null
}

export interface TmtFiguresOut {
  source: 'tmt'
  purchases: number
  revenue_minor: number
  new_customers: number
  cpa_minor: number | null
  roas: number | null
  cost_per_new_customer_minor: number | null
}

export interface AdCampaignRowOut {
  id: string
  name: string | null
  status: string | null
  effective_status: string | null
  spend_minor: number
  impressions: number
  clicks: number
  ctr: number | null
  cpc_minor: number | null
  tmt: TmtFiguresOut
  provider_reported: ProviderFiguresOut
}

export interface ReconciliationPartOut {
  purchases: number
  revenue_minor: number
}

export interface OverviewReconciliationOut {
  attributed_revenue_minor: number
  purchases: number
  campaigns: ReconciliationPartOut
  unmatched_paid: ReconciliationPartOut
  ad_campaigns_without_spend: ReconciliationPartOut
  campaign_credit_off_spend_rows: ReconciliationPartOut
}

export interface AdvertisingOverviewOut {
  data: {
    provider: AdProvider
    kpis: Record<string, AdKpiOut>
    not_available: Record<string, AdNotAvailable>
    provider_reported: {
      source: 'meta'
      current: ProviderFiguresOut | null
      previous: ProviderFiguresOut | null
    }
    campaigns: AdCampaignRowOut[]
    top_campaigns: AdCampaignRowOut[]
    limit: number
    reconciliation: OverviewReconciliationOut | null
    data_quality?: DataQualityOut | null
  }
  meta: {
    provider: AdProvider
    currency: string
    timezone: string
    range: DayRange
    previous_range: DayRange
    previous_unavailable?: 'currency_rate_unavailable' | null
    environments: Environment[]
    non_production: boolean
    website_id: string | null
    model: AttributionModel
    lookback_days: number
    attribution_windows: string[]
    as_of: AdvertisingFreshnessOut
  }
}

export interface AdTrendPointOut {
  date: string
  end_date: string
  spend_minor: number
  attributed_revenue_minor: number
  purchases: number
  /** Absent from an API older than the all-sales line. */
  orders?: number | null
  revenue_gross_minor?: number | null
}

export interface AdvertisingOverviewTrendOut {
  data: {
    provider: AdProvider
    granularity: TrendGranularity
    points: AdTrendPointOut[]
    totals: {
      spend_minor: number
      attributed_revenue_minor: number
      purchases: number
      orders?: number | null
      revenue_gross_minor?: number | null
    } | null
    not_available: AdNotAvailable | null
    sales_unavailable?: 'currency_rate_unavailable' | null
  }
  meta: {
    provider: AdProvider
    currency: string
    timezone: string
    range: DayRange
    environments: Environment[]
    non_production: boolean
    website_id: string | null
    model: AttributionModel
    lookback_days: number
  }
}

export interface MetaReportedOut {
  provider_attributed: true
  purchases: number | null
  revenue_minor: number | null
  not_available?: MetaNotAvailable | null
}

export interface TmtAttributedOut {
  tmt_attributed: true
  model: AttributionModel
  purchases: number
  revenue_minor: number
}

export interface ReconciliationDiffOut {
  purchases: number | null
  revenue_minor: number | null
  purchases_pct: number | null
  revenue_pct: number | null
  purchases_null_reason?: DiffNullReason | null
  revenue_null_reason?: DiffNullReason | null
  purchases_pct_null_reason?: DiffNullReason | null
  revenue_pct_null_reason?: DiffNullReason | null
}

export interface ReconciliationFiguresOut {
  meta: MetaReportedOut
  tmt: TmtAttributedOut
  diff: ReconciliationDiffOut
}

export interface ReconciliationMetaOnlyOut {
  provider_attributed: true
  purchases: number | null
  revenue_minor: number | null
  purchases_null_reason?: MetaOnlyNullReason | null
  revenue_null_reason?: MetaOnlyNullReason | null
}

export interface ReconciliationTotalOut extends ReconciliationFiguresOut {
  meta_only: ReconciliationMetaOnlyOut
}

export interface ReconciliationRowOut extends ReconciliationFiguresOut {
  id: string
  name: string | null
  spend_minor: number
  delivery_in_range: boolean
}

export interface ReconciliationLineOut extends ReconciliationFiguresOut {
  kind: ReconciliationLineKind
}

export interface AdAttributionReconciliationOut {
  start: string
  end: string
  level: AdvertisingLevel
  currency: string
  model: AttributionModel
  attribution_windows: string[]
  tmt_lookback_days: number
  rows: ReconciliationRowOut[]
  lines: ReconciliationLineOut[]
  objects_total: ReconciliationFiguresOut
  total: ReconciliationTotalOut
  causes: ReconciliationCause[]
  as_of: AdvertisingFreshnessOut
}

// --- mappers ----------------------------------------------------------------------

function isKpiKey(key: string): key is AdKpiKey {
  return (AD_KPI_KEYS as readonly string[]).includes(key)
}

function providerFiguresFromWire(body: ProviderFiguresOut): ProviderFigures {
  return {
    source: 'meta',
    purchases: body.purchases,
    revenueMinor: body.revenue_minor,
    roas: body.roas,
    cpaMinor: body.cpa_minor,
  }
}

function tmtFiguresFromWire(body: TmtFiguresOut): TmtFigures {
  return {
    source: 'tmt',
    purchases: body.purchases,
    revenueMinor: body.revenue_minor,
    newCustomers: body.new_customers,
    cpaMinor: body.cpa_minor,
    roas: body.roas,
    costPerNewCustomerMinor: body.cost_per_new_customer_minor,
  }
}

function campaignFromWire(row: AdCampaignRowOut): AdCampaignRow {
  return {
    id: row.id,
    name: row.name,
    status: row.status,
    effectiveStatus: row.effective_status,
    spendMinor: row.spend_minor,
    impressions: row.impressions,
    clicks: row.clicks,
    ctr: row.ctr,
    cpcMinor: row.cpc_minor,
    tmt: tmtFiguresFromWire(row.tmt),
    providerReported: providerFiguresFromWire(row.provider_reported),
  }
}

function partFromWire(part: ReconciliationPartOut): ReconciliationPart {
  return { purchases: part.purchases, revenueMinor: part.revenue_minor }
}

function overviewReconciliationFromWire(body: OverviewReconciliationOut): OverviewReconciliation {
  return {
    attributedRevenueMinor: body.attributed_revenue_minor,
    purchases: body.purchases,
    campaigns: partFromWire(body.campaigns),
    unmatchedPaid: partFromWire(body.unmatched_paid),
    adCampaignsWithoutSpend: partFromWire(body.ad_campaigns_without_spend),
    campaignCreditOffSpendRows: partFromWire(body.campaign_credit_off_spend_rows),
  }
}

/**
 * The KPI map as a list in the API's display order. A key this client does not know
 * (one the API added later) is left out rather than shown unlabelled.
 */
function kpisFromWire(kpis: Record<string, AdKpiOut>): AdKpi[] {
  return AD_KPI_KEYS.flatMap((key) => {
    const kpi = kpis[key]
    if (!kpi) return []
    return [
      {
        key,
        unit: kpi.unit,
        source: kpi.source,
        current: kpi.current,
        previous: kpi.previous,
        deltaPct: kpi.delta_pct,
        nullReason: kpi.null_reason ?? null,
      },
    ]
  })
}

function notAvailableFromWire(body: Record<string, AdNotAvailable>): Partial<Record<AdKpiKey, AdNotAvailable>> {
  const result: Partial<Record<AdKpiKey, AdNotAvailable>> = {}
  for (const [key, reason] of Object.entries(body)) if (isKpiKey(key)) result[key] = reason
  return result
}

export function advertisingOverviewFromWire(body: AdvertisingOverviewOut): AdvertisingOverview {
  const { data, meta } = body
  return {
    provider: data.provider,
    kpis: kpisFromWire(data.kpis),
    notAvailable: notAvailableFromWire(data.not_available),
    providerReported: {
      current: data.provider_reported.current ? providerFiguresFromWire(data.provider_reported.current) : null,
      previous: data.provider_reported.previous ? providerFiguresFromWire(data.provider_reported.previous) : null,
    },
    campaigns: data.campaigns.map(campaignFromWire),
    topCampaigns: data.top_campaigns.map(campaignFromWire),
    limit: data.limit,
    reconciliation: data.reconciliation ? overviewReconciliationFromWire(data.reconciliation) : null,
    dataQuality: dataQualityFromWire(data.data_quality),
    meta: {
      provider: meta.provider,
      currency: meta.currency,
      timezone: meta.timezone,
      range: meta.range,
      previousRange: meta.previous_range,
      previousUnavailable: meta.previous_unavailable ?? null,
      environments: meta.environments,
      nonProduction: meta.non_production,
      websiteId: meta.website_id,
      model: meta.model,
      lookbackDays: meta.lookback_days,
      attributionWindows: meta.attribution_windows,
      asOf: freshnessFromWire(meta.as_of),
    },
  }
}

export function advertisingOverviewTrendFromWire(body: AdvertisingOverviewTrendOut): AdvertisingOverviewTrend {
  const { data, meta } = body
  return {
    provider: data.provider,
    granularity: data.granularity,
    points: data.points.map((point) => ({
      date: point.date,
      endDate: point.end_date,
      spendMinor: point.spend_minor,
      attributedRevenueMinor: point.attributed_revenue_minor,
      purchases: point.purchases,
      orders: point.orders ?? null,
      revenueGrossMinor: point.revenue_gross_minor ?? null,
    })),
    totals: data.totals
      ? {
          spendMinor: data.totals.spend_minor,
          attributedRevenueMinor: data.totals.attributed_revenue_minor,
          purchases: data.totals.purchases,
          orders: data.totals.orders ?? null,
          revenueGrossMinor: data.totals.revenue_gross_minor ?? null,
        }
      : null,
    notAvailable: data.not_available,
    salesUnavailable: data.sales_unavailable ?? null,
    meta: {
      currency: meta.currency,
      timezone: meta.timezone,
      range: meta.range,
      model: meta.model,
      lookbackDays: meta.lookback_days,
    },
  }
}

function figuresFromWire(body: ReconciliationFiguresOut): ReconciliationFigures {
  return {
    meta: {
      providerAttributed: true,
      purchases: body.meta.purchases,
      revenueMinor: body.meta.revenue_minor,
      notAvailable: body.meta.not_available ?? null,
    },
    tmt: {
      tmtAttributed: true,
      model: body.tmt.model,
      purchases: body.tmt.purchases,
      revenueMinor: body.tmt.revenue_minor,
    },
    diff: {
      purchases: body.diff.purchases,
      revenueMinor: body.diff.revenue_minor,
      purchasesPct: body.diff.purchases_pct,
      revenuePct: body.diff.revenue_pct,
      purchasesNullReason: body.diff.purchases_null_reason ?? null,
      revenueNullReason: body.diff.revenue_null_reason ?? null,
      purchasesPctNullReason: body.diff.purchases_pct_null_reason ?? null,
      revenuePctNullReason: body.diff.revenue_pct_null_reason ?? null,
    },
  }
}

export function adAttributionReconciliationFromWire(body: AdAttributionReconciliationOut): AdAttributionReconciliation {
  return {
    start: body.start,
    end: body.end,
    level: body.level,
    currency: body.currency,
    model: body.model,
    attributionWindows: body.attribution_windows,
    tmtLookbackDays: body.tmt_lookback_days,
    rows: body.rows.map((row) => ({
      id: row.id,
      name: row.name,
      spendMinor: row.spend_minor,
      deliveryInRange: row.delivery_in_range,
      ...figuresFromWire(row),
    })),
    lines: body.lines.map((line) => ({ kind: line.kind, ...figuresFromWire(line) })),
    objectsTotal: figuresFromWire(body.objects_total),
    total: {
      ...figuresFromWire(body.total),
      metaOnly: {
        providerAttributed: true,
        purchases: body.total.meta_only.purchases,
        revenueMinor: body.total.meta_only.revenue_minor,
        purchasesNullReason: body.total.meta_only.purchases_null_reason ?? null,
        revenueNullReason: body.total.meta_only.revenue_null_reason ?? null,
      },
    },
    causes: body.causes.map((cause) => ({ code: cause.code, text: cause.text })),
    asOf: freshnessFromWire(body.as_of),
  }
}

// --- query strings ----------------------------------------------------------------

/** The Overview's scope (range, website, environments), the provider and the model. */
function overviewParams(
  query: { filters: GlobalFilters; provider: AdProvider; model: AttributionModel },
  now: Date,
): URLSearchParams {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  params.set('provider', query.provider)
  params.set('model', query.model)
  return params
}

/** The API's `limit` is 1–20. */
const MAX_TOP_CAMPAIGNS = 20

export function advertisingOverviewSearch(query: AdOverviewQuery, now: Date): string {
  const params = overviewParams(query, now)
  params.set('limit', String(Math.min(MAX_TOP_CAMPAIGNS, Math.max(1, Math.trunc(query.limit)))))
  return params.toString()
}

export function advertisingOverviewTrendSearch(query: AdOverviewTrendQuery, now: Date): string {
  const params = overviewParams(query, now)
  params.set('granularity', query.granularity)
  return params.toString()
}

/** The reconciliation's and its CSV's query string, so the download is the table on screen. */
export function adReconciliationSearch(query: AdReconciliationQuery): string {
  return new URLSearchParams({ from: query.from, to: query.to, level: query.level, model: query.model }).toString()
}
